import { EyeOutlined, LoadingOutlined } from '@ant-design/icons';
import React, { useState } from 'react';
import { IQuestionOption } from '@/features/assistant/types';
import { useCurrency } from '@/features/product-configurator/hooks/useCurrency';
import { ImagePreviewModal } from '../ImagePreviewModal';

interface SingleChoiceCardProps {
  options: IQuestionOption[];
  selectedId?: string;
  onSelect: (option: IQuestionOption) => void;
  isDisabled?: boolean;
  isProcessing?: boolean;
  // When true, only the "Add to Cart" option is disabled with a spinner —
  // blend stages (DBR/colorize/grill) are still running and the cart item
  // must not be created with a stale/incomplete image.
  isBlending?: boolean;
}

export const SingleChoiceCard: React.FC<SingleChoiceCardProps> = ({
  selectedId,
  onSelect,
  isDisabled = false,
  isBlending = false,
  options
}) => {
  const { formatPrice } = useCurrency();
  const [preview, setPreview] = useState<{ src: string; alt: string } | null>(null);

  return (
    <div className="w-full mb-6">
      <div
        className="w-full"
        style={{ opacity: isDisabled ? 0.4 : 1 }}
      >
        <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(220px,1fr))]">
          {options.map((option) => {
            const isSelected = selectedId === option.optionId;
            const isAddToCartOption = option.responseValue === 'addToCart';
            const optionDisabled = isDisabled || (isAddToCartOption && isBlending);
            return (
              <button
                key={option.optionId}
                className={`rounded-xl border p-4 flex items-start gap-4 transition-all text-left ${isSelected
                  ? 'border-primary-500 bg-primary-50'
                  : 'bg-white border-neutral-300 hover:border-neutral-400'
                  } ${isAddToCartOption && isBlending ? 'opacity-60 cursor-wait' : ''}`}
                onClick={() => !optionDisabled && onSelect(option)}
                disabled={optionDisabled}
              >
                {option.thumbnail && (
                  <div className="relative w-20 h-20 bg-neutral-100 rounded-lg p-2 flex items-center justify-center flex-shrink-0 group">
                    <img
                      src={option.thumbnail}
                      alt={option.optionText}
                      className="w-full h-full object-contain"
                    />
                    {/* Preview button — appears on hover, doesn't trigger card selection */}
                    <button
                      type="button"
                      className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={e => {
                        e.stopPropagation();
                        setPreview({ src: option.thumbnail!, alt: option.optionText });
                      }}
                      title="Preview image"
                    >
                      <EyeOutlined style={{ color: 'white', fontSize: 18 }} />
                    </button>
                  </div>
                )}

                <div className="flex-1 flex flex-col gap-1">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm md:text-base font-semibold text-text-200">
                      {option.optionText}
                    </span>
                    {isAddToCartOption && isBlending && (
                      <LoadingOutlined style={{ color: '#9ca3af', fontSize: 16 }} />
                    )}
                  </div>

                  {isAddToCartOption && isBlending ? (
                    <span className="text-xs md:text-sm text-text-secondary leading-relaxed">
                      Finishing window image, please wait...
                    </span>
                  ) : option?.description && typeof option.description === 'string' && (
                    <span className="text-xs md:text-sm text-text-secondary leading-relaxed">
                      {option.description}
                    </span>
                  )}

                  {option?.price > 0 ? (
                    <span className="text-xs md:text-sm font-bold text-primary-500 mt-1">
                      From {formatPrice(option.price)}
                    </span>
                  ) : option.priceModifier > 0 ? (
                    <span className="text-xs md:text-sm font-bold text-primary-500 mt-1">
                      +{formatPrice(option.priceModifier)}
                    </span>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {preview && (
        <ImagePreviewModal
          src={preview.src}
          alt={preview.alt}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
};
